<template>
  <div v-if="!isAdminPage">
    <UIBaseTransition>
      <button
        v-if="!showAgentInfo"
        class="fixed right-4 desktop:bottom-6 flex aspect-square size-[60px] items-center justify-center rounded-full bg-gold-primary-100 z-50 overflow-hidden safe-area-bottom"
        @click="showAgentInfo = true"
      >
        <img
          v-if="isExplorePage && isUpgraded && !authStore.getIsLoadingAgent && authStore.getAgent?.profileImageUrl"
          :src="agentImage"
          alt="Agent"
          class="w-full h-full object-cover scale-x-[-1]"
        />
        <img
          v-else-if="isExplorePage && !isUpgraded"
          src="/images/logos/logo-black-vertical.svg"
          alt="Dubai Insiders Club"
          class="h-8 w-auto"
        />
        <UIProgressSpinner
          v-else-if="isExplorePage && isUpgraded && authStore.getIsLoadingAgent"
          width="24"
          height="24"
          color="white"
        />
        <UIIcon v-else name="chat" class="text-white" />
      </button>
      <div v-else class="fixed inset-x-0 top-0 z-50 h-dvh w-screen backdrop-blur-sm" @click.self="showAgentInfo = false">
        <div
          ref="infoPanel"
          class="fixed bottom-40 right-4 desktop:bottom-[88px] desktop:right-4 rounded-2xl overflow-hidden"
          :class="isUpgraded ? 'w-[294px] h-[432px]' : 'w-[294px] h-[290px]'"
        >
          <div v-if="!isUpgraded" class="h-full rounded-2xl flex flex-col overflow-hidden">
            <div class="h-[210px] bg-[#FFB648] rounded-t-2xl px-4 flex flex-col relative">
              <div class="absolute top-4 left-4 flex-shrink-0 size-[68px] rounded-full border border-black-70 bg-transparent flex items-center justify-center p-2 z-10">
                <img src="/images/logos/logo-black-vertical.svg" alt="Dubai Insiders Club" class="h-8 w-auto" />
              </div>
              <div class="flex flex-col items-start text-left mt-[90px]">
                <h2 
                  class="text-[24px] font-bold text-black-100" 
                  style="font-family: 'Satoshi', sans-serif; font-weight: 700; line-height: 120%; letter-spacing: 0%;"
                >
                  Hello there 👋
                </h2>
                <p 
                  class="text-[14px] text-black-100 mt-2" 
                  style="font-family: 'Open Sans', sans-serif; font-weight: 500; line-height: 134%; letter-spacing: 0%;"
                >
                  Contact our portfolio advisors to get detailed and personalised information.
                </p>
              </div>
            </div>
            <div class="h-[80px] bg-white rounded-b-2xl px-4 flex items-center justify-center">
              <a
                :href="whatsAppLink"
                target="_blank"
                class="flex items-center justify-center gap-2 w-full bg-[#25D366] text-white font-bold py-2 px-4 rounded-xl hover:bg-[#22C55E] transition-colors"
              >
                <UIIcon name="whatsapp" class="text-white" />
                <span>Start WhatsApp Chat</span>
              </a>
            </div>
          </div>
          <div v-else class="h-full bg-[#F5F1E8] rounded-2xl flex flex-col">
            <div class="relative h-[266px] overflow-hidden">
              <img
                :src="agentImage"
                :alt="agentName"
                class="w-full h-full object-cover object-top"
              />
            </div>
            <div class="bg-white rounded-b-2xl px-4 pt-4 pb-4 flex flex-col gap-3">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <h3 class="text-lg font-bold text-black-100">{{ agentName }}</h3>
                  <UIIcon name="glowing-check" class="text-[#FF7A3D] align-middle" size="xs" />
                </div>
                <div class="flex items-center gap-1.5">
                  <div class="size-2 rounded-full bg-[#25D366]"></div>
                  <span class="text-sm text-black-100">Available</span>
                </div>
              </div>
              <p class="text-sm text-black-60">{{ agentTitle }}</p>
              <div class="flex items-center gap-2">
                <div class="flex gap-0.5">
                  <UIIcon v-for="i in 5" :key="i" name="star-filled" class="text-gold-primary-100" size="sm" />
                </div>
                <span class="text-sm text-black-60">4.8 rating</span>
              </div>
              <a
                :href="whatsAppLinkAgent"
                target="_blank"
                class="relative flex items-center justify-center gap-2 w-full bg-[#25D366] text-white font-bold py-2 px-4 rounded-xl hover:bg-[#22C55E] transition-colors mt-2"
              >
                <UIIcon name="whatsapp" class="text-white" />
                <span>Chat with {{ agentFirstName }}</span>
              </a>
            </div>
          </div>
        </div>
        <button
          class="fixed right-4 desktop:bottom-6 flex aspect-square size-[60px] items-center justify-center rounded-full bg-gold-primary-100 z-50 safe-area-bottom"
          @click="showAgentInfo = false"
        >
          <UIIcon name="chevron-big-down" class="text-white" />
        </button>
      </div>
    </UIBaseTransition>
  </div>
</template>

<script setup lang="ts">
import { USER_ROLES, USER_STATUSES } from '~/constants/auth'

const authStore = useAuthStore()
const route = useRoute()
const showAgentInfo = ref(false)
const infoPanel = ref(null)

const FORCE_UPGRADED_FOR_TESTING: boolean | null = null

const localePath = useLocalePath()

const isAdminPage = computed(() => {
  const path = route.path
  return path.includes('/admin') || path.includes('/signup') || path.includes('/login') || path.includes('/reset')
})

const isExplorePage = computed(() => {
  const routeName = route.name?.toString() || ''
  const homePath = localePath('/')
  const currentPath = route.path
  const isHomePage = routeName.startsWith('index') || currentPath === homePath || currentPath === `${homePath}/` || currentPath === '/'
  
  if (!isHomePage) {
    return false
  }
  
  const explore = route.query?.explore
  return explore === '1' || explore === 'true' || explore === 'map'
})

const isUpgraded = computed(() => {
  if (FORCE_UPGRADED_FOR_TESTING !== null) {
    return FORCE_UPGRADED_FOR_TESTING
  }
  if (authStore.getUser?.role === USER_ROLES.ADMIN) {
    return true
  }
  return authStore.getUser?.status === USER_STATUSES.MEMBER
})

const agentName = computed(() => {
  const agent = authStore.getAgent
  if (agent?.firstName && agent?.lastName) {
    return `${agent.firstName} ${agent.lastName}`
  }
  if (agent?.firstName) {
    return agent.firstName
  }
  return 'Frank'
})

const agentFirstName = computed(() => {
  const agent = authStore.getAgent
  return agent?.firstName || 'Frank'
})

const agentImage = computed(() => {
  const agent = authStore.getAgent
  if (agent?.profileImageUrl) {
    return agent.profileImageUrl
  }
  return '/images/agent-frank.webp'
})

const agentTitle = computed(() => {
  return 'Senior Investment Portfolio Advisor'
})

const whatsAppLink = computed(() => {
  const phone = '971527033506'
  const text = encodeURIComponent('Hello, I would like to get information about investment opportunities.')
  return `https://wa.me/${phone}?text=${text}`
})

const whatsAppLinkAgent = computed(() => {
  const agent = authStore.getAgent
  
  if (!agent) {
    const phone = '971527033506'
    const text = encodeURIComponent('Hello Frank, I would like to discuss investment opportunities.')
    return `https://wa.me/${phone}?text=${text}`
  }
  
  const phone = agent.phone ? agent.phone.replace(/\D/g, '') : '971527033506'
  const agentName = agent.firstName || 'Frank'
  const text = encodeURIComponent(`Hello ${agentName}, I would like to discuss investment opportunities.`)
  return `https://wa.me/${phone}?text=${text}`
})

onClickOutside(infoPanel, () => {
  showAgentInfo.value = false
})
</script>

<style scoped>
.safe-area-bottom {
  bottom: calc(5rem + env(safe-area-inset-bottom, 0px));
}

@media (min-width: 1024px) {
  .safe-area-bottom {
    bottom: 1.5rem;
  }
}
</style>
